"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Code, Copy, Check, ExternalLink, Maximize2, Minimize2, Eye, EyeOff, Sun, Moon, Palette, Leaf, ListOrdered } from "lucide-react"
import { type AccentColor, type ThemeMode, ACCENT_THEMES } from "@/components/embed/lib/embed-themes"
import { trackEvent } from "@/lib/analytics"

/**
 * Attribution targets per embed. The credit line lives in the embedder's
 * parent-page HTML (outside the iframe) so the link actually passes SEO
 * equity; in-iframe links pass nothing. Tools without a dedicated landing
 * page credit their own tool page.
 */
const ATTRIBUTION_TARGETS: Record<string, { path: string; label: string }> = {
  "soil-temperature-map": {
    path: "/soil-temperature-map",
    label: "WhatGrassIsThis.com soil temperature map",
  },
  "soil-temperature": {
    path: "/tools/soil-temperature",
    label: "WhatGrassIsThis.com soil temperature tracker",
  },
}

function attributionSnippet(baseUrl: string, toolSlug: string, toolName: string): string {
  const target = ATTRIBUTION_TARGETS[toolSlug] ?? {
    path: `/tools/${toolSlug}`,
    label: `${toolName} by WhatGrassIsThis.com`,
  }
  const href = `${baseUrl}${target.path}?utm_source=embed&utm_medium=widget&utm_campaign=${toolSlug}`
  // Deliberately tiny and low-contrast: the link is the point (it's the SEO
  // carrier in the embedder's parent HTML), not the visual credit. Must stay a
  // real rendered link — display:none/sr-only links read as hidden-link spam.
  return [
    `<p style="font-size: 11px; margin: 2px 0 0; text-align: right; color: #9ca3af;">`,
    `  <a href="${href}" style="color: inherit;">Data: WhatGrassIsThis.com</a>`,
    `</p>`,
  ].join('\n')
}

interface EmbedCodeModalProps {
  toolName: string
  toolSlug: string
  hasMiniVersion?: boolean
}

const ACCENT_OPTIONS: { value: AccentColor; label: string; color: string }[] = [
  { value: "green", label: "Green", color: "#22C55E" },
  { value: "blue", label: "Blue", color: "#3B82F6" },
  { value: "purple", label: "Purple", color: "#8B5CF6" },
  { value: "orange", label: "Orange", color: "#F97316" },
  { value: "neutral", label: "Neutral", color: "#6B7280" },
]

export function EmbedCodeModal({ toolName, toolSlug, hasMiniVersion = false }: EmbedCodeModalProps) {
  const [copied, setCopied] = useState<string | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<"full" | "mini">("full")
  const [showPreview, setShowPreview] = useState(false)
  const [selectedTheme, setSelectedTheme] = useState<ThemeMode>("light")
  const [selectedAccent, setSelectedAccent] = useState<AccentColor>("green")

  const baseUrl = "https://whatgrassisthis.com"

  // Build URL with theme params
  const buildEmbedUrl = (version: "full" | "mini") => {
    const basePath = version === "mini"
      ? `${baseUrl}/embed/${toolSlug}/mini`
      : `${baseUrl}/embed/${toolSlug}`

    const params = new URLSearchParams()
    if (selectedTheme !== "light") params.set("theme", selectedTheme)
    if (selectedAccent !== "green") params.set("accent", selectedAccent)

    const queryString = params.toString()
    return queryString ? `${basePath}?${queryString}` : basePath
  }

  const embedUrl = buildEmbedUrl(selectedVersion)
  const height = selectedVersion === "mini" ? "450" : "700"
  const previewHeight = selectedVersion === "mini" ? 450 : 700

  // Use current origin for preview (works in dev and production)
  const getPreviewUrl = () => {
    if (typeof window !== "undefined") {
      const origin = window.location.origin
      const basePath = selectedVersion === "mini"
        ? `${origin}/embed/${toolSlug}/mini`
        : `${origin}/embed/${toolSlug}`

      const params = new URLSearchParams()
      if (selectedTheme !== "light") params.set("theme", selectedTheme)
      if (selectedAccent !== "green") params.set("accent", selectedAccent)

      const queryString = params.toString()
      return queryString ? `${basePath}?${queryString}` : basePath
    }
    return embedUrl
  }

  // Clean, left-aligned embed code for easy copying. Both snippets end with
  // the parent-HTML credit line (the SEO-equity carrier).
  const attribution = attributionSnippet(baseUrl, toolSlug, toolName)

  const basicEmbedCode = [
    `<iframe`,
    `  src="${embedUrl}"`,
    `  width="100%"`,
    `  height="${height}"`,
    `  frameborder="0"`,
    `  style="border: 1px solid #e5e7eb; border-radius: 8px;"`,
    `  title="${toolName} by WhatGrassIsThis.com"`,
    `></iframe>`,
    attribution,
  ].join('\n')

  const embedCodeWithHelper = [
    `<iframe`,
    `  src="${embedUrl}"`,
    `  width="100%"`,
    `  frameborder="0"`,
    `  style="border: 1px solid #e5e7eb; border-radius: 8px;"`,
    `  title="${toolName} by WhatGrassIsThis.com"`,
    `></iframe>`,
    attribution,
    ``,
    `<script src="${baseUrl}/embed-helper.js"></script>`,
  ].join('\n')

  const handleCopy = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(id)
      setTimeout(() => setCopied(null), 2000)
      trackEvent("embed_code_copied", {
        tool: toolSlug,
        snippet: id,
        version: selectedVersion,
      })
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 hover:bg-gray-50"
        >
          <Code className="w-4 h-4" />
          Embed
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-green-600" />
            Embed {toolName}
          </DialogTitle>
          <DialogDescription>
            Customize and add this calculator to your website or blog.
          </DialogDescription>
        </DialogHeader>

        {/* Quick Steps Guide */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <ListOrdered className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-gray-900">How to Embed</h3>
          </div>
          <ol className="text-sm text-gray-700 space-y-1.5">
            <li className="flex gap-2">
              <span className="font-medium text-gray-500">1.</span>
              <span>{hasMiniVersion ? "Choose a version (Full or Mini)" : "Preview the calculator below"}</span>
            </li>
            {hasMiniVersion && selectedVersion === "mini" && (
              <li className="flex gap-2">
                <span className="font-medium text-gray-500">2.</span>
                <span>Customize the theme and accent color to match your site</span>
              </li>
            )}
            <li className="flex gap-2">
              <span className="font-medium text-gray-500">{hasMiniVersion && selectedVersion === "mini" ? "3." : "2."}</span>
              <span>Copy the <strong>Auto-Resize Embed</strong> code (recommended)</span>
            </li>
            <li className="flex gap-2">
              <span className="font-medium text-gray-500">{hasMiniVersion && selectedVersion === "mini" ? "4." : "3."}</span>
              <span>Paste it into your website&apos;s HTML where you want the calculator</span>
            </li>
          </ol>
        </div>

        <div className="space-y-6 mt-4">
          {/* Version Selector */}
          {hasMiniVersion && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Choose Version</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSelectedVersion("full")}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedVersion === "full"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Maximize2 className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-gray-900">Full Version</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    All options including fertilizer type, soil type, and detailed results
                  </p>
                  <p className="text-xs text-gray-500 mt-1">~700px height • Standard styling</p>
                </button>
                <button
                  onClick={() => setSelectedVersion("mini")}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedVersion === "mini"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Minimize2 className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-gray-900">Mini Version</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Customizable</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Compact widget with essential fields. Full theme customization!
                  </p>
                  <p className="text-xs text-gray-500 mt-1">~450px height • Great for sidebars</p>
                </button>
              </div>
            </div>
          )}

          {/* Theme & Color Customization - Only for Mini version */}
          {selectedVersion === "mini" ? (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Palette className="w-4 h-4 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Customize Appearance</h3>
              </div>

              {/* Theme Mode */}
              <div className="mb-4">
                <label className="text-sm text-gray-600 mb-2 block">Theme</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTheme("light")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedTheme === "light"
                        ? "border-green-500 bg-white"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <Sun className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium">Light</span>
                  </button>
                  <button
                    onClick={() => setSelectedTheme("dark")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedTheme === "dark"
                        ? "border-green-500 bg-gray-900 text-white"
                        : "border-gray-200 bg-gray-800 text-gray-300 hover:border-gray-500"
                    }`}
                  >
                    <Moon className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium">Dark</span>
                  </button>
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Accent Color</label>
                <div className="flex gap-2 flex-wrap">
                  {ACCENT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedAccent(option.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                        selectedAccent === option.value
                          ? "border-gray-900 bg-white shadow-xs"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : hasMiniVersion ? (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <Leaf className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-800">
                  <span className="font-medium">Full version uses standard site styling</span>
                  <span className="text-green-700"> for a consistent experience. Choose Mini for custom themes.</span>
                </p>
              </div>
            </div>
          ) : null}

          {/* Live Preview */}
          <div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 w-full p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
            >
              {showPreview ? (
                <EyeOff className="w-4 h-4 text-gray-600" />
              ) : (
                <Eye className="w-4 h-4 text-green-600" />
              )}
              <span className="font-semibold text-gray-900">
                {showPreview ? "Hide Preview" : "Show Live Preview"}
              </span>
              {!showPreview && (
                <span className="text-xs text-gray-500 ml-auto">
                  See exactly how it will look
                </span>
              )}
            </button>

            {showPreview && (
              <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-100 max-w-2xl mx-auto">
                <div className="bg-gray-200 px-3 py-2 text-xs text-gray-600 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <span className="ml-2 font-mono text-gray-500 truncate text-[10px]">
                    {getPreviewUrl()}
                  </span>
                </div>
                <div
                  className="relative bg-white"
                  style={{
                    maxHeight: selectedVersion === "mini" ? "500px" : "750px",
                    overflow: "auto"
                  }}
                >
                  <iframe
                    key={`${selectedVersion}-${selectedTheme}-${selectedAccent}`}
                    src={getPreviewUrl()}
                    width="100%"
                    height={previewHeight}
                    frameBorder="0"
                    title={`${toolName} Preview`}
                    className="border-0"
                  />
                </div>
                <div className="bg-gray-100 px-3 py-2 text-xs text-gray-500 text-center flex items-center justify-center gap-2">
                  <span>
                    {selectedVersion === "mini" ? "Mini" : "Full"} •{" "}
                    {selectedTheme === "dark" ? "Dark" : "Light"} •{" "}
                    {ACCENT_OPTIONS.find(o => o.value === selectedAccent)?.label} theme
                  </span>
                  {selectedVersion === "full" && (
                    <span className="text-gray-400">• Scroll to see more</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Basic Embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Basic Embed</h3>
              <span className="text-xs text-gray-500">Fixed height</span>
            </div>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 text-sm p-4 rounded-lg whitespace-pre-wrap break-all">
                <code>{basicEmbedCode}</code>
              </pre>
              <Button
                onClick={() => handleCopy(basicEmbedCode, "basic")}
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
              >
                {copied === "basic" ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Auto-resize Embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Auto-Resize Embed</h3>
              <span className="text-xs text-green-600 font-medium">Recommended</span>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Includes a script that automatically adjusts the iframe height based on content.
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 text-sm p-4 rounded-lg whitespace-pre-wrap break-all">
                <code>{embedCodeWithHelper}</code>
              </pre>
              <Button
                onClick={() => handleCopy(embedCodeWithHelper, "helper")}
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
              >
                {copied === "helper" ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Preview Links */}
          <div className="pt-4 border-t flex items-center gap-4 flex-wrap">
            <a
              href={getPreviewUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-green-600 hover:text-green-700"
            >
              <ExternalLink className="w-4 h-4" />
              Open preview in new tab
            </a>
          </div>

          {/* Usage Notes */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">Usage Notes</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• The embed is free to use on any website</li>
              <li>• Please keep the small source link under the widget</li>
              <li>• Works on WordPress, Squarespace, Wix, and custom sites</li>
              <li>• Theme settings are saved in the embed URL</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
